import { CalculContextBase, CalculContextState } from '@batical/types/src'
import { ProjetCommon_Address, ProjetCommon_State } from '@batical/types/src/ProjectCommonState'
import { produce } from 'immer'
import { v4 as uuidv4 } from 'uuid'
import { ProjectCustomType } from './ProjectCustomType'

/* A type that is used to define the shape of the state. */
export type CustomProjectState = CalculContextBase &
  ProjetCommon_State & {
    id: string
    isProject: true
    calculs: { [key: string]: CalculContextState & { id: string; section: string } }
    folders: { [key: string]: ProjectDocumentFolder }
    hasUnsavedChanges: boolean
  }

export type ProjectDocument = {
  id: string
  name: string
  description: string
  saveDate: number
  uri: string
  type: string | null
}

export type ProjectDocumentFolder = {
  id: string
  name: string
  creationDate: number
  lastUpdateDate: number
  type: 'classic' | 'evo'
  documents: { [key: string]: ProjectDocument }
  printable: boolean
}

export type ProjectCustomState = CustomProjectState

const initialState: CustomProjectState = {
  calcul_code: 'PCUSTOM',
  bati_version: 1,
  id: '',
  name: '',
  deleted: 0,
  saveDate: 0,
  isProject: true,
  calculs: {},
  folders: {},
  hasUnsavedChanges: false,
  user: '',
  projectName: '',
  address: undefined,
  addressComponents: undefined,
  avancement: '0',
  description: ''
}

type Action =
  | { type: ProjectCustomType.CLEAN_CUSTOM_PROJECT }
  | { type: ProjectCustomType.LOAD_PROJECT_CUSTOM; payload: CustomProjectState }
  | {
      type: ProjectCustomType.ADD_CALCUL_CUSTOM_PROJECT
      payload: { calcul: CalculContextState; section: string; title: string }
    }
  | { type: ProjectCustomType.REMOVE_CALCUL_CUSTOM_PROJECT; payload: string }
  | { type: ProjectCustomType.UPDATE_PROJECT_NAME; payload: string }
  | { type: ProjectCustomType.UPDATE_PROJECT_DESCRIPTION; payload: string }
  | { type: ProjectCustomType.UPDATE_PROJECT_ADDRESS; payload: string | null }
  | { type: ProjectCustomType.UPDATE_PROJECT_ADDRESS_COMPONENTS; payload: ProjetCommon_Address | null }
  | { type: ProjectCustomType.UPDATE_PROJECT_AVANCEMENT; payload: string }
  | { type: ProjectCustomType.ADD_DOCUMENT_FOLDER; payload: ProjectDocumentFolder }
  | {
      type: ProjectCustomType.UPDATE_DOCUMENT_FOLDER
      payload: Partial<ProjectDocumentFolder> & { id: string }
    }
  | { type: ProjectCustomType.REMOVE_DOCUMENT_FOLDER; payload: string }
  | { type: ProjectCustomType.ADD_DOCUMENT; payload: { doc: ProjectDocument; folderId: string } }
  | {
      type: ProjectCustomType.UPDATE_DOCUMENT
      payload: { doc: Partial<ProjectDocument> & { id: string }; folderId: string }
    }
  | { type: ProjectCustomType.REMOVE_DOCUMENT; payload: { id: string; folderId: string } }

export const projectCustomReducer = (state: CustomProjectState = initialState, action: Action) =>
  produce(state, draft => {
    switch (action.type) {
      case ProjectCustomType.CLEAN_CUSTOM_PROJECT:
        return { ...initialState }
      case ProjectCustomType.LOAD_PROJECT_CUSTOM:
        return { ...action.payload, hasUnsavedChanges: false }

      case ProjectCustomType.ADD_CALCUL_CUSTOM_PROJECT: {
        const uuid: string = uuidv4() as string
        draft.calculs[uuid] = {
          ...action.payload.calcul,
          id: uuid,
          name: action.payload.title,
          section: action.payload.section
        }
        draft.hasUnsavedChanges = true
        return
      }

      case ProjectCustomType.REMOVE_CALCUL_CUSTOM_PROJECT:
        delete draft.calculs[action.payload]
        draft.hasUnsavedChanges = true
        return

      case ProjectCustomType.UPDATE_PROJECT_NAME:
        draft.projectName = action.payload
        draft.hasUnsavedChanges = true
        return

      case ProjectCustomType.UPDATE_PROJECT_DESCRIPTION:
        draft.description = action.payload
        draft.hasUnsavedChanges = true
        return

      case ProjectCustomType.UPDATE_PROJECT_ADDRESS:
        draft.address = action.payload
        draft.hasUnsavedChanges = true
        return

      case ProjectCustomType.UPDATE_PROJECT_ADDRESS_COMPONENTS:
        draft.addressComponents = action.payload
        draft.hasUnsavedChanges = true
        return

      case ProjectCustomType.UPDATE_PROJECT_AVANCEMENT:
        draft.avancement = action.payload
        draft.hasUnsavedChanges = true
        return

      case ProjectCustomType.ADD_DOCUMENT_FOLDER:
        draft.folders[action.payload.id] = action.payload
        return

      case ProjectCustomType.UPDATE_DOCUMENT_FOLDER:
        draft.folders[action.payload.id] = {
          ...draft.folders[action.payload.id],
          ...action.payload
        }
        return

      case ProjectCustomType.REMOVE_DOCUMENT_FOLDER:
        delete draft.folders[action.payload]
        return

      case ProjectCustomType.ADD_DOCUMENT:
        draft.folders[action.payload.folderId].documents[action.payload.doc.id] = action.payload.doc
        return

      case ProjectCustomType.UPDATE_DOCUMENT:
        draft.folders[action.payload.folderId].documents[action.payload.doc.id] = {
          ...draft.folders[action.payload.folderId].documents[action.payload.doc.id],
          ...action.payload.doc
        }
        return

      case ProjectCustomType.REMOVE_DOCUMENT:
        delete draft.folders[action.payload.folderId].documents[action.payload.id]
        return
    }
  })

export default projectCustomReducer
